import {Component, OnDestroy, OnInit} from '@angular/core';
import {debounceTime, Subject, takeUntil} from 'rxjs';
import {DropdownOption} from '../../../../../../models/dropdown-option.model';
import * as Validation
  from '../../../../../shared/constants/validation.constants';
import {FormControl, FormGroup} from '@angular/forms';
import {Store} from '@ngrx/store';
import * as fromRoot from '../../../../../../state/app.state';
import {CommonService} from '../../../../../shared/services/common.service';
import {
  FetchClinicFilterRequest,
  FetchGroupListFilterRequest,
  FetchUserStatusListFilterRequest
} from '../../../core/clients-list.actions';
import {
  clinicFilter,
  groupListFilter,
  userStatusListFilter
} from '../../../core/clients-list.selectors';
import {ClientListService} from '../../../services/client-list.service';
import {Filter} from '../../../../../../models/filter';
import {roles} from '../../../../../shared/constants/common.constants';

@Component({
  selector: 'app-participant-or-caregiver-information-filter',
  templateUrl: './participant-or-caregiver-information-filter.component.html',
  styleUrls: ['./participant-or-caregiver-information-filter.component.css']
})
export class ParticipantOrCaregiverInformationFilterComponent implements OnInit,
                                                                         OnDestroy {

  roleFilterList: DropdownOption[] = [
    {
      key: roles.adult,
      value: 'Adult',
    },
    {
      key: roles.participant,
      value: 'Participant',
    },
  ];
  statusFilterList: DropdownOption[] = [];
  errorMessages: any = {};
  validation = Validation;
  errorMessageMap = {
    'clientId': {},
    'groupId': {},
    'role': {},
    'status': {},
    'searchText': {},
    'searchClient': {},
    'searchGroup': {},
  }
  form = new FormGroup({
    clientId: new FormControl(null),
    groupId: new FormControl({value: null, disabled: true}),
    role: new FormControl(null),
    status: new FormControl(null),
    searchText: new FormControl(null),
    searchClient: new FormControl(''),
    searchGroup: new FormControl(''),
  })
  clientOptions: Filter = {
    list: [],
    total: 0,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  };
  groupOptions: Filter = {
    list: [],
    total: 0,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  };
  private readonly OnDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private commonService: CommonService,
    private clientListService: ClientListService,
  ) {
    this.fetchClientsFilterList();
    this.store.dispatch(FetchUserStatusListFilterRequest())
  }

  ngOnInit() {
    this.store.select(clinicFilter)
      .pipe(takeUntil(this.OnDestroy))
      .subscribe((data) => {
        this.clientOptions.list.push(...data?.list || []);
        this.clientOptions.hasNext = data?.hasNext
        this.clientOptions.loading = data?.loading;
      });

    this.form.controls.searchClient.valueChanges
      .pipe(debounceTime(1000), takeUntil(this.OnDestroy))
      .subscribe(data => {
        this.clientOptions.pageNumber = 1;
        this.clientOptions.list = [];
        this.fetchClientsFilterList();
      });

    this.store.select(groupListFilter)
      .pipe(takeUntil(this.OnDestroy))
      .subscribe((data) => {
        this.groupOptions.list.push(...data?.list || []);
        this.groupOptions.hasNext = data?.hasNext
        this.groupOptions.loading = data?.loading;
      });

    this.form.controls.searchGroup.valueChanges
      .pipe(debounceTime(1000), takeUntil(this.OnDestroy))
      .subscribe(data => {
        this.groupOptions.pageNumber = 1;
        this.groupOptions.list = [];
        this.fetchGroupFilterList();
      });

    this.store.select(userStatusListFilter)
      .pipe(takeUntil(this.OnDestroy))
      .subscribe((data) => {
        this.statusFilterList = data.list;
      });

    this.form.controls.clientId.valueChanges
      .pipe(takeUntil(this.OnDestroy))
      .subscribe(data => {
        if (data) {
          this.groupOptions.pageNumber = 1;
          this.groupOptions.list = [];
          this.fetchGroupFilterList(data);
          this.form.controls.groupId.enable();
        }
      });
  }

  apply() {
    // ERROR CHECK IN THE FORM//
    if (this.form.invalid) {
      this.checkForErrors();
      return;
    }
    if (!this.form.dirty) { // check is there any change
      return;
    }
    // changes just applied, so marking the form as un-dirty to avoid applying same filters again without changing anything
    this.form.markAsPristine();

    // EXTRACT VALUE FROM FORM//
    const {searchClient, searchGroup, ...filters} = this.form.getRawValue();
    this.clientListService.onParticipantCaregiverFilterChange.next(filters)
  }

  checkForErrors(currentField ?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField))
    };
  }

  fetchClientsFilterList() {
    const payload: any = {
      pageNumber: this.clientOptions?.pageNumber,
      limit: this.clientOptions?.limit,
      shouldInclude: this.form.value.clientId,
    };
    if (this.form.controls?.searchClient?.value.trim()) {
      payload.search = this.form.controls.searchClient.value;
    }
    this.store.dispatch(FetchClinicFilterRequest({payload}));
  }

  clientsOnNextPage() {
    this.clientOptions.pageNumber = this.clientOptions.pageNumber + 1
    this.fetchClientsFilterList();
  }

  fetchGroupFilterList(clientId?) {
    const payload: any = {
      pageNumber: this.groupOptions?.pageNumber,
      limit: this.groupOptions?.limit,
      shouldInclude: this.form.value.groupId,
    };
    if (this.form.controls?.searchGroup?.value.trim()) {
      payload.search = this.form.controls.searchGroup.value;
    }
    if (clientId) {
      payload.clientId = clientId;
    }
    this.store.dispatch(FetchGroupListFilterRequest({payload}));
  }

  groupOnNextPage() {
    this.groupOptions.pageNumber = this.groupOptions.pageNumber + 1
    this.fetchGroupFilterList();
  }

  ngOnDestroy() {
    this.OnDestroy.next(null);
    this.OnDestroy.complete();
  }

}
